import { XMLParser, XMLBuilder } from 'fast-xml-parser';
import { IKMLDocument, IKMLElement } from './kml';

// This file is an attempt to replace the outdated and unmaintened
// xml2js library with fast-xml-parser. fast-xml-parser provides a
// much simpler API and is actively maintained.
interface ExtendedData {
  Data: {
    name: string;
    value: string | number | boolean | KMLElementSchema;
  }[];
}

interface KMLElementSchema {
  open: number;
  name: string;
  description?: string;
  ExtendedData?: ExtendedData;
  [key: string]: unknown;
}

interface VisibleElement extends KMLElementSchema {
  visibility: number;
}

interface Folder extends VisibleElement {}

interface Point {
  coordinates: string;
}

interface LineString {
  coordinates: string;
}

interface Polygon {
  outerBoundaryIs: {
    LinearRing: {
      coordinates: string;
    };
  };
}

interface Placemark extends VisibleElement {
  styleUrl: string;
  Polygon?: Polygon;
  Point?: Point;
  LineString?: LineString;
  // TODO more geo types?
}

interface Style {
  id: string;
  LineStyle?: {
    color: string;
    width: number;
  };
  PolyStyle?: {
    color: string;
    fill: number;
    outline?: number;
  };
  IconStyle?: {
    scale?: number;
    Icon: {
      href: string;
    };
    // hotSpot?: string;
    hotSpot?: {
      x: number;
      y: number;
      xunits: string;
      yunits: string;
    };
  };
  LabelStyle?: {
    color?: string;
    scale?: number;
  };
}

interface StyleMap {
  id: string;
  Pair: {
    key: string;
    styleUrl: string;
  }[];
}

interface KMLDocumentSchema extends KMLElementSchema {
  Style?: Style[];
  StyleMap?: StyleMap[];
}

export class KMLElement implements KMLElementSchema, IKMLElement {
  [key: string]: unknown;
  open: number;
  name: string;
  description?: string;
  ExtendedData?: ExtendedData;
  tagName: string;
  doc: IKMLDocument;
  parentElement: IKMLElement;
  element: IKMLElement;
  getElementContent() {
    throw new Error('Method not implemented.');
  }
  append(el: IKMLElement): void {
    throw new Error('Method not implemented.');
  }
  extendFromText(text: string): void {
    throw new Error('Method not implemented.');
  }
  getText(): string {
    throw new Error('Method not implemented.');
  }
  setText(text: string): void {
    throw new Error('Method not implemented.');
  }
  find(tag: string, include_grandchildren?: boolean, where?: (element: IKMLElement) => boolean): IKMLElement {
    throw new Error('Method not implemented.');
  }
  findAll(tag: string, include_grandchildren: boolean, where?: (element: IKMLElement) => boolean): IKMLElement[] {
    throw new Error('Method not implemented.');
  }
  getExtendedData(): { [key: string]: any } {
    throw new Error('Method not implemented.');
  }
  putExtendedData(key: string, val: string | boolean | number | IKMLElement) {
    throw new Error('Method not implemented.');
  }
  findParent(tag: string, where: (element: IKMLElement) => boolean): IKMLElement {
    throw new Error('Method not implemented.');
  }
}

export class KMLDocument implements KMLDocumentSchema, IKMLDocument {
  [key: string]: unknown;
  Style?: Style[];
  StyleMap?: StyleMap[];
  open: number;
  name: string;
  description?: string;
  ExtendedData?: ExtendedData;
  document: any;
  getRoot(): IKMLElement {
    throw new Error('Method not implemented.');
  }
  find(tag: string, include_grandchildren?: boolean, where?: (element: IKMLElement) => boolean): IKMLElement {
    throw new Error('Method not implemented.');
  }
  findAll(tag: string, include_grandchildren: boolean, where?: (element: IKMLElement) => boolean): IKMLElement[] {
    throw new Error('Method not implemented.');
  }
  createElement(tag: string): IKMLElement {
    throw new Error('Method not implemented.');
  }
  toString(ugly?: boolean): string {
    throw new Error('Method not implemented.');
  }
  toKMZ(filename?: string): Promise<Uint8Array> {
    throw new Error('Method not implemented.');
  }
}

export function kmlToString(document: KMLDocumentSchema) {
  const builder = new XMLBuilder();
  const content = builder.build({
    kml: {
      xmlns: 'http://www.opengis.net/kml/2.2',
      Document: document,
    },
  });

  return content;
}

export function toKMLObject(kml: string) {
  const parser = new XMLParser({
    ignoreAttributes: false,
    attributeNamePrefix: '',
    parseAttributeValue: true,
  });
  const content = parser.parse(kml);

  return content;
}

export function exampleRogoKML() {
  const sampleDoc: KMLDocumentSchema = {
    open: 1,
    name: 'Miller farm__Fox Creek Organ__Early Bird Fertilizer __6.67in__Reg__Midwest',
    description: '',
    Placemark: {
      visibility: 1,
      name: 'pullin',
      Point: {
        coordinates: '-89.13785875768714,40.60871494162333,0',
      },
      styleUrl: '#pullin_location',
    },
    ExtendedData: {
      Data: [
        {
          value: 'v5.2-458-g323b1f9d',
          name: 'rogo_version',
        },
        {
          value: 'recPYjHHIMfAS1hc4',
          name: 'job_id',
        },
        {
          value: '',
          name: 'attachment_id',
        },
        {
          value: 'S1A+S3Composite*',
          name: 'test_package',
        },
        {
          value: '',
          name: 'event_id',
        },
        {
          value: 'Early Bird Fertilizer',
          name: 'client',
        },
        {
          value: 'Fox Creek Organics',
          name: 'grower',
        },
        {
          value: '',
          name: 'grower_id',
        },
        {
          value: '',
          name: 'farm',
        },
        {
          value: 'Miller farm',
          name: 'field',
        },
        {
          value: 13272,
          name: 'billing_account',
        },
        {
          value: 'Rogo',
          name: 'sampling_company_name',
        },
        {
          value: '',
          name: 'sampling_company_id',
        },
        {
          value: 'n/a',
          name: 'response_email',
        },
        {
          value: 'Midwest Labs, NE',
          name: 'lab_name',
        },
        {
          value: 14,
          name: 'lab_code',
        },
        {
          value: '',
          name: 'in_field_notes_ops',
        },
        {
          value: false,
          name: 'tilled_field_at_sampling',
        },
        {
          value: 'None',
          name: 'boundary_change_type',
        },
        {
          value: '',
          name: 'boundary_change_notes',
        },
        {
          value: '13611 B St, Omaha, NE 68144',
          name: 'lab_address',
        },
        {
          value: 'UPS',
          name: 'lab_primary_delivery',
        },
        {
          value: '',
          name: 'field_id',
        },
        {
          value: 20,
          name: 'add_on_freq',
        },
        {
          value: '1,3,7,29',
          name: 'micros_list',
        },
        {
          value: 'drive',
          name: 'sample_order_type',
        },
        {
          value: 'MWAS',
          name: 'lab_submittal_id',
        },
        {
          value: true,
          name: 'lab_qrs_required',
        },
        {
          value: '',
          name: 'auto_renumber',
        },
        {
          value: '',
          name: 'lab_instructions',
        },
        {
          value: 'Rogo',
          name: 'submitter_notified_id',
        },
        {
          value: 'Grid',
          name: 'sites_type',
        },
        {
          value: '[]',
          name: 'job_flags',
        },
        {
          value: 1721675416.367,
          name: 'last_modified',
        },
        {
          value: 1721661635.805,
          name: 'sample_date',
        },
        {
          value: '95576e09ba28bb90b9fe614c2bffcdc33799ebab',
          name: 'nav_checksum',
        },
        {
          value: true,
          name: 'active',
        },
        {
          value: '',
          name: 'settings_changes',
        },
        {
          value: '',
          name: 'settings_start',
        },
        {
          value: '{}',
          name: 'settings_end',
        },
        {
          value:
            '[[{"secs":1721660228,"nsecs":165573829},286,0,{"y":-89.13827254833333,"x":40.608264633333334,"z":2.424026160430908},0,"1",null,null,null,null],[{"secs":1721660235,"nsecs":17165268},286,0,{"y":-89.13828180166666,"x":40.608272495,"z":2.3668077552795412},0,"1",null,null,null,null],[{"secs":1721660241,"nsecs":970598786},286,0,{"y":-89.138290885,"x":40.608279895,"z":2.333540231323242},0,"1",null,null,null,null],[{"secs":1721660248,"nsecs":848696738},286,-2,{"y":-89.13830073333334,"x":40.60828829,"z":2.3477553672790528},0,"1",null,null,null,null],[{"secs":1721660255,"nsecs":775311577},286,-1,{"y":-89.13831150166666,"x":40.60829538666667,"z":2.3641818412780764},0,"1",null,null,null,null],[{"secs":1721660263,"nsecs":455692495},286,-7,{"y":-89.138320785,"x":40.60830266166667,"z":2.3762631225585937},0,"1",null,null,null,null],[{"secs":1721660317,"nsecs":872293379},286,2,{"y":-89.13827446333333,"x":40.60726716833334,"z":3.989135607910156},0,"2",null,null,null,null],[{"secs":1721660325,"nsecs":51818339},286,0,{"y":-89.13828139166667,"x":40.60725925333333,"z":4.045848906707763},0,"2",null,null,null,null],[{"secs":1721660332,"nsecs":556248554},286,0,{"y":-89.13829018833333,"x":40.60725027,"z":4.086581726837158},0,"2",null,null,null,null],[{"secs":1721660340,"nsecs":39913412},286,0,{"y":-89.13829966,"x":40.60724080666667,"z":4.046329250335694},0,"2",null,null,null,null],[{"secs":1721660350,"nsecs":722502},286,-2,{"y":-89.13830445166667,"x":40.60723774,"z":4.029070961761475},0,"2",null,null,null,null],[{"secs":1721660356,"nsecs":702570836},286,-3,{"y":-89.13831057666667,"x":40.60723272333333,"z":4.007571324157715},0,"2",null,null,null,null],[{"secs":1721660363,"nsecs":930827516},286,-2,{"y":-89.138318695,"x":40.607225471666666,"z":3.9932281143188475},0,"2",null,null,null,null],[{"secs":1721660413,"nsecs":930757298},286,0,{"y":-89.13823743333333,"x":40.606050208333336,"z":5.53470267162323},0,"3",null,null,null,null],[{"secs":1721660421,"nsecs":335114437},286,1,{"y":-89.13822871666666,"x":40.60604202166667,"z":5.462561528968811},0,"3",null,null,null,null],[{"secs":1721660428,"nsecs":339052215},286,1,{"y":-89.13821874,"x":40.60603497,"z":5.467243082046509},0,"3",null,null,null,null],[{"secs":1721660435,"nsecs":742318452},286,1,{"y":-89.13820926833333,"x":40.606027571666665,"z":5.494832885742188},0,"3",null,null,null,null],[{"secs":1721660443,"nsecs":97271821},286,0,{"y":-89.13819914166666,"x":40.606019081666666,"z":5.50409873828888},0,"3",null,null,null,null],[{"secs":1721660450,"nsecs":201094804},286,0,{"y":-89.13818838,"x":40.606012066666665,"z":5.514988679504395},0,"3",null,null,null,null],[{"secs":1721660488,"nsecs":1205094},286,-4,{"y":-89.13827444333333,"x":40.60517875,"z":3.982762001800537},0,"4",null,null,null,null],[{"secs":1721660495,"nsecs":154709880},286,2,{"y":-89.13828308833334,"x":40.60516943333333,"z":4.0500162338256835},0,"4",null,null,null,null],[{"secs":1721660502,"nsecs":409231356},286,0,{"y":-89.13829011666667,"x":40.605162398333334,"z":4.059861547470093},0,"4",null,null,null,null],[{"secs":1721660509,"nsecs":411641781},286,1,{"y":-89.13829740666667,"x":40.60515440166667,"z":4.026205674743652},0,"4",null,null,null,null],[{"secs":1721660517,"nsecs":92991753},286,0,{"y":-89.13831039166666,"x":40.60514566166667,"z":3.926819542694092},0,"4",null,null,null,null],[{"secs":1721660524,"nsecs":749959163},286,1,{"y":-89.13831874333333,"x":40.60513970666667,"z":3.93187673034668},0,"4",null,null,null,null],[{"secs":1721660562,"nsecs":373804284},286,0,{"y":-89.13811878333334,"x":40.60428280166666,"z":5.541336619186401},0,"5",null,null,null,null],[{"secs":1721660569,"nsecs":578074443},286,1,{"y":-89.13810997,"x":40.604275208333334,"z":5.489170475769043},0,"5",null,null,null,null],[{"secs":1721660576,"nsecs":881627079},286,-1,{"y":-89.13810023833334,"x":40.60426677666667,"z":5.443378404426575},0,"5",null,null,null,null],[{"secs":1721660584,"nsecs":60669517},286,-3,{"y":-89.13809139833333,"x":40.604258951666665,"z":5.4633513401031495},0,"5",null,null,null,null],[{"secs":1721660591,"nsecs":138589057},286,2,{"y":-89.13808175333334,"x":40.604252046666666,"z":5.49608233833313},0,"5",null,null,null,null],[{"secs":1721660598,"nsecs":618366489},286,0,{"y":-89.13807003,"x":40.60424366833333,"z":5.534236373519898},0,"5",null,null,null,null],[{"secs":1721660646,"nsecs":109468809},286,0,{"y":-89.13654587666667,"x":40.604020555,"z":0.6893836364746094},0,"6",null,null,null,null],[{"secs":1721660653,"nsecs":589331429},286,3,{"y":-89.13653437833334,"x":40.604029096666665,"z":0.7410998571395874},0,"6",null,null,null,null],[{"secs":1721660661,"nsecs":194016658},286,0,{"y":-89.13652224,"x":40.60403789833333,"z":0.7620983823776245},0,"6",null,null,null,null],[{"secs":1721660668,"nsecs":448388530},286,0,{"y":-89.136512435,"x":40.60404396,"z":0.7762807676315308},0,"6",null,null,null,null],[{"secs":1721660676,"nsecs":154044369},286,1,{"y":-89.136504075,"x":40.60405098333333,"z":0.7933614568710328},0,"6",null,null,null,null],[{"secs":1721660684,"nsecs":863576721},286,3,{"y":-89.13649427666667,"x":40.60405805833334,"z":0.820419860458374},0,"6",null,null,null,null],[{"secs":1721660741,"nsecs":239228773},286,2,{"y":-89.13690019833334,"x":40.60513257,"z":2.387086297607422},0,"7",null,null,null,null],[{"secs":1721660751,"nsecs":154431484},286,1,{"y":-89.136909965,"x":40.60514085,"z":2.3324866172790526},0,"7",null,null,null,null],[{"secs":1721660760,"nsecs":366353922},286,0,{"y":-89.13692014333333,"x":40.60514911166667,"z":2.326698529815674},0,"7",null,null,null,null],[{"secs":1721660769,"nsecs":251455665},286,0,{"y":-89.13693163833334,"x":40.60515827166667,"z":2.3282468658447266},0,"7",null,null,null,null],[{"secs":1721660778,"nsecs":338253688},286,0,{"y":-89.13694142,"x":40.60516592333333,"z":2.3552736503601075},0,"7",null,null,null,null],[{"secs":1721660787,"nsecs":349059918},286,2,{"y":-89.13694921833333,"x":40.60517198833333,"z":2.3584459419250487},0,"7",null,null,null,null],[{"secs":1721660830,"nsecs":597424771},286,0,{"y":-89.13662785166666,"x":40.60600667333333,"z":0.8308241327285767},0,"8",null,null,null,null],[{"secs":1721660838,"nsecs":579949004},285,1,{"y":-89.13662061333333,"x":40.606013858333334,"z":0.8755945419311524},0,"8",null,null,null,null],[{"secs":1721660846,"nsecs":937381494},285,1,{"y":-89.13661171666666,"x":40.606020955,"z":0.8596435522079467},0,"8",null,null,null,null],[{"secs":1721660856,"nsecs":23760984},285,0,{"y":-89.13660020833333,"x":40.60603043333333,"z":0.832938483428955},0,"8",null,null,null,null],[{"secs":1721660864,"nsecs":834840368},285,0,{"y":-89.13659028,"x":40.606038845,"z":0.8199984281539917},0,"8",null,null,null,null],[{"secs":1721660873,"nsecs":142596610},285,-6,{"y":-89.13658031833333,"x":40.606046105,"z":0.8205060640335083},0,"8",null,null,null,null],[{"secs":1721660919,"nsecs":177024791},286,-1,{"y":-89.13690492166667,"x":40.607221736666666,"z":2.4272703437805174},0,"9",null,null,null,null],[{"secs":1721660926,"nsecs":331077926},286,0,{"y":-89.13691512666666,"x":40.607229935,"z":2.375578553009033},0,"9",null,null,null,null],[{"secs":1721660933,"nsecs":961408237},286,0,{"y":-89.13692481666666,"x":40.607239355,"z":2.3265864318847655},0,"9",null,null,null,null],[{"secs":1721660941,"nsecs":165301999},286,-1,{"y":-89.13693606666666,"x":40.607247705,"z":2.3443221351623533},0,"9",null,null,null,null],[{"secs":1721660948,"nsecs":820995159},286,1,{"y":-89.13694562333333,"x":40.60725472166666,"z":2.351414126586914},0,"9",null,null,null,null],[{"secs":1721660956,"nsecs":426862195},286,-1,{"y":-89.136956175,"x":40.60726262,"z":2.3680016914367674},0,"9",null,null,null,null],[{"secs":1721661003,"nsecs":289420347},286,1,{"y":-89.13693852666667,"x":40.60826277333334,"z":0.860559373664856},0,"10",null,null,null,null],[{"secs":1721661011,"nsecs":296863614},286,0,{"y":-89.13692996666667,"x":40.60827216333333,"z":0.9191408603668213},0,"10",null,null,null,null],[{"secs":1721661018,"nsecs":802079756},286,3,{"y":-89.13692277833333,"x":40.60828061833333,"z":0.9061365684509277},0,"10",null,null,null,null],[{"secs":1721661027,"nsecs":85068047},286,1,{"y":-89.13691169333333,"x":40.608289123333336,"z":0.848503454208374},0,"10",null,null,null,null],[{"secs":1721661034,"nsecs":640471259},286,2,{"y":-89.13690341833333,"x":40.60829652333334,"z":0.8242718622207642},0,"10",null,null,null,null],[{"secs":1721661042,"nsecs":697775147},286,1,{"y":-89.13689314666667,"x":40.60830428166667,"z":0.7993439184188843},0,"10",null,null,null,null],[{"secs":1721661097,"nsecs":895477338},286,2,{"y":-89.135572045,"x":40.60726384,"z":5.522865156745911},0,"11",null,null,null,null],[{"secs":1721661105,"nsecs":524648718},286,0,{"y":-89.13556539833333,"x":40.607258315,"z":5.489805386734009},0,"11",null,null,null,null],[{"secs":1721661114,"nsecs":40056755},286,-1,{"y":-89.13555413333333,"x":40.607247533333336,"z":5.47776337966919},0,"11",null,null,null,null],[{"secs":1721661122,"nsecs":141295678},286,0,{"y":-89.13554133333334,"x":40.607239953333334,"z":5.512081588745117},0,"11",null,null,null,null],[{"secs":1721661130,"nsecs":73198351},286,0,{"y":-89.13553182,"x":40.607233025,"z":5.5264132160186765},0,"11",null,null,null,null],[{"secs":1721661138,"nsecs":255434403},286,2,{"y":-89.13552207833334,"x":40.607226338333334,"z":5.534018035507202},0,"11",null,null,null,null],[{"secs":1721661185,"nsecs":68891749},286,0,{"y":-89.13536955666666,"x":40.60610253833333,"z":3.937778513336182},0,"12",null,null,null,null],[{"secs":1721661193,"nsecs":50284196},286,1,{"y":-89.13537869166667,"x":40.606092456666666,"z":4.054581894683838},0,"12",null,null,null,null],[{"secs":1721661201,"nsecs":333765766},286,1,{"y":-89.13538780333333,"x":40.60608250666667,"z":4.104813483428955},0,"12",null,null,null,null],[{"secs":1721661209,"nsecs":520441002},286,2,{"y":-89.13539617,"x":40.60607409333333,"z":4.068885564041138},0,"12",null,null,null,null],[{"secs":1721661217,"nsecs":699535898},286,0,{"y":-89.13540567666666,"x":40.606066678333335,"z":4.020063187408447},0,"12",null,null,null,null],[{"secs":1721661225,"nsecs":430409977},286,2,{"y":-89.13541538666666,"x":40.60605856,"z":3.996966487121582},0,"12",null,null,null,null],[{"secs":1721661264,"nsecs":135598944},286,0,{"y":-89.135573,"x":40.605178635,"z":5.5687498886108395},0,"13",null,null,null,null],[{"secs":1721661272,"nsecs":294104171},285,2,{"y":-89.13556395833334,"x":40.605170523333335,"z":5.511026510238647},0,"13",null,null,null,null],[{"secs":1721661280,"nsecs":250440669},285,1,{"y":-89.13555279166667,"x":40.605162055,"z":5.499626203918457},0,"13",null,null,null,null],[{"secs":1721661288,"nsecs":508520476},285,0,{"y":-89.13554386666667,"x":40.60515408,"z":5.499847404289246},0,"13",null,null,null,null],[{"secs":1721661296,"nsecs":540883082},285,2,{"y":-89.13553321333333,"x":40.60514637666667,"z":5.522816363525391},0,"13",null,null,null,null],[{"secs":1721661304,"nsecs":322163351},285,0,{"y":-89.13552382,"x":40.605139665,"z":5.536185239982605},0,"13",null,null,null,null],[{"secs":1721661344,"nsecs":533495998},286,0,{"y":-89.13553668666667,"x":40.604137433333335,"z":3.9753856384277344},0,"14",null,null,null,null],[{"secs":1721661352,"nsecs":716166037},286,2,{"y":-89.13554619,"x":40.604128295,"z":4.0501670734405515},0,"14",null,null,null,null],[{"secs":1721661360,"nsecs":799014921},286,1,{"y":-89.13555272333333,"x":40.60412023166667,"z":4.069281900787353},0,"14",null,null,null,null],[{"secs":1721661369,"nsecs":157104908},286,2,{"y":-89.13556258333334,"x":40.604112385,"z":4.020269010925293},0,"14",null,null,null,null],[{"secs":1721661377,"nsecs":666491318},286,2,{"y":-89.13557299666667,"x":40.6041026,"z":3.9812027488708495},0,"14",null,null,null,null],[{"secs":1721661386,"nsecs":100004960},286,0,{"y":-89.13558394166667,"x":40.604094866666664,"z":3.9622262466430667},0,"14",null,null,null,null],[{"secs":1721661435,"nsecs":71737958},286,-1,{"y":-89.13434864333334,"x":40.604335283333334,"z":5.739587035465241},0,"15",null,null,null,null],[{"secs":1721661443,"nsecs":279210014},286,1,{"y":-89.134334945,"x":40.60432741,"z":5.701824710655212},0,"15",null,null,null,null],[{"secs":1721661451,"nsecs":437511485},286,3,{"y":-89.13432447666666,"x":40.604320655,"z":5.659786921501159},0,"15",null,null,null,null],[{"secs":1721661459,"nsecs":369406168},286,0,{"y":-89.13431369833333,"x":40.60431370166667,"z":5.6210324567794805},0,"15",null,null,null,null],[{"secs":1721661467,"nsecs":828188829},286,0,{"y":-89.13430289333333,"x":40.604306101666666,"z":5.5813680950164795},0,"15",null,null,null,null],[{"secs":1721661475,"nsecs":534530716},286,2,{"y":-89.134293125,"x":40.60429935166667,"z":5.573667353248596},0,"15",null,null,null,null],[{"secs":1721661583,"nsecs":668717674},286,-3,{"y":-89.13454580166666,"x":40.607087441666664,"z":0.8318113800048829},0,"16",null,null,null,null],[{"secs":1721661592,"nsecs":327725950},286,0,{"y":-89.13453769,"x":40.607095408333336,"z":0.8852778327941895},0,"16",null,null,null,null],[{"secs":1721661600,"nsecs":862762307},286,1,{"y":-89.13452944833334,"x":40.60710401666667,"z":0.9129569021224976},0,"16",null,null,null,null],[{"secs":1721661609,"nsecs":372471102},286,1,{"y":-89.134518825,"x":40.607113643333335,"z":0.8877826623916626},0,"16",null,null,null,null],[{"secs":1721661617,"nsecs":729978314},286,0,{"y":-89.13450934166667,"x":40.60712066,"z":0.8464838941574097},0,"16",null,null,null,null],[{"secs":1721661626,"nsecs":490750031},286,2,{"y":-89.13449968333333,"x":40.60712864,"z":0.8387557935714722},0,"16",null,null,null,null]]',
          name: 'cores',
        },
        {
          value: 'Bdy,Pts',
          name: 'required_fields',
        },
        {
          value: false,
          name: 'allowed_to_renumber',
        },
        {
          value: false,
          name: 'allowed_to_create',
        },
        {
          value: false,
          name: 'allowed_to_move',
        },
        {
          value: '0.75 in',
          name: 'core_diameter',
        },
        {
          value: 0,
          name: 'field_aligned_sample_path',
        },
        {
          value: false,
          name: 'manual_hole_depth_measurement',
        },
        {
          value: false,
          name: 'manual_plunge_depth_measurement',
        },
        {
          value: false,
          name: 'manual_core_length_measurement',
        },
        {
          value: false,
          name: 'manual_core_loss_measurement',
        },
        {
          value:
            '{"plotContainerOpen":false,"acres":50.49,"actual":0,"angle":0,"angleRadians":0,"startCorner":"SW","pathDirection":"N/S","shiftNorth":0,"shiftEast":0,"densityIncrement":0.1,"angleIncrement":5,"boundaryIncrement":5,"shiftIncrement":5,"xStart":0,"xCount":4,"xIncrement":1,"yStart":0,"yCount":4,"yIncrement":1,"plotWidth":10,"plotHeight":40,"alleyWidthX":0,"alleyWidthY":0,"plotWidthIncrement":5,"plotLengthIncrement":5,"fieldAngle":0,"numberOfSamples":1}',
          name: 'plot_settings',
        },
        {
          value: false,
          name: 'plot_mission',
        },
        {
          value: false,
          name: 'disable_core_line_alteration',
        },
        {
          value:
            '[{"_instance_id":1,"_version":7,"_refs":{"Sample":[20,21,22,23,24,25,26,27,28,29,30,31,32,33,34,35,36,37,38,39,40,41,42,43,44,45,46,47,48,49,50,51,52,53,54,55,56,57,58,59,60,61,62,63]},"_uid":"5CGZASF1S4","_closedTimestamp":"2024-07-22T15:22:07.777Z","_needsUpdated":true,"_labName":"Midwest Labs, NE","_username":"christopherp","_trackingNumber":"1ZR7F8930390516476","_trackingNumberTime":"","_jobBoxCountIndex":1,"_dailyBoxCountIndex":1,"_reprint_reason":"","uid":"5CGZASF1S4","closedTimestamp":"2024-07-22T15:22:07.777Z","needsUpdated":true,"labName":"Midwest Labs, NE","username":"christopherp","trackingNumber":"1ZR7F8930390516476","trackingNumberTime":"","jobBoxCountIndex":1,"dailyBoxCountIndex":1,"reprint_reason":""}]',
          name: 'boxes',
        },
      ],
    },
    Folder: {
      name: 'target',
      open: 1,
      visibility: 1,
      Folder: [
        {
          name: 'boundaries',
          open: 1,
          visibility: 1,
          Placemark: {
            name: 'field',
            visibility: 1,
            ExtendedData: {
              Data: [
                {
                  value: 2,
                  name: 'poly_id',
                },
                {
                  value: 2,
                  name: 'zone_id',
                },
              ],
            },
            Polygon: {
              outerBoundaryIs: {
                LinearRing: {
                  coordinates:
                    '-89.13890503078821,40.60863453870872,0 -89.13612626594275,40.60869969898514,0 -89.13605116419357,40.6086345388221,0 -89.1360404354083,40.60790148257669,0 -89.13543962138952,40.607901482600525,0 -89.1354288925495,40.608268011729,0 -89.13489245145838,40.60833317227373,0 -89.13417362040158,40.60832502724027,0 -89.13406633238961,40.60418720742839,0 -89.13408779003835,40.60408131497595,0 -89.1341521629725,40.60400800471634,0 -89.13418434944134,40.60393469437759,0 -89.1342809088371,40.60393469437378,0 -89.1343238241249,40.60391840317503,0 -89.13447402763107,40.60388582076311,0 -89.13507484165063,40.6038613839244,0 -89.13520358751013,40.603893966337154,0 -89.13532160454707,40.603942839929495,0 -89.1354396215877,40.60391840313075,0 -89.1355790962747,40.603836947080545,0 -89.1377141318122,40.60376363647062,0 -89.13786433531527,40.60380436454404,0 -89.137960894711,40.603812510153155,0 -89.13814328468638,40.603714762725495,0 -89.13869045459775,40.60372290832755,0 -89.13890503078821,40.60863453870872,0',
                },
              },
            },
            styleUrl: '#field_boundary',
          },
        },
        {
          name: 'paths',
          open: 1,
          visibility: 1,
          Placemark: {
            visibility: 1,
            name: 'path',
            LineString: {
              coordinates:
                '-89.13826475704097,40.608267042129285,0 -89.13827518808938,40.60827461466877,0 -89.13828561914015,40.60828218720725,0 -89.13829605019326,40.60828975974471,0 -89.13830648124873,40.60829733228117,0 -89.13831691230656,40.608304904816606,0 -89.13826599487929,40.60726284788717,0 -89.13827593081675,40.607254898122505,0 -89.13828586675184,40.60724694835703,0 -89.1382958026846,40.607238998590745,0 -89.13830573861497,40.60723104882366,0 -89.13831567454302,40.607223099055766,0 -89.13824491140926,40.60604690486079,0 -89.13823448072172,40.60603933230699,0 -89.13822405003654,40.60603175975218,0 -89.1382136193537,40.60602418719637,0 -89.13820318867323,40.606016614639536,0 -89.13819275799511,40.6060090420817,0 -89.13826599572951,40.60517684787011,0 -89.13827593136855,40.605168898110584,0 -89.13828586700525,40.60516094835024,0 -89.13829580263959,40.60515299858909,0 -89.13830573827157,40.60514504882713,0 -89.1383156739012,40.60513709906438,0 -89.1381269106181,40.604279904912154,0 -89.13811648022703,40.60427233233826,0 -89.1381060498383,40.60426475976334,0 -89.13809561945193,40.60425718718741,0 -89.13808518906792,40.60424961461047,0 -89.13807475868626,40.60424204203253,0 -89.13654767202817,40.6040210995065,0 -89.13653773634194,40.6040290491196,0 -89.13652780065334,40.6040369987319,0 -89.1365178649624,40.60404494834339,0 -89.1365079292691,40.6040528979541,0 -89.13649799357346,40.60406084756398,0 -89.13689575722277,40.60513804181795,0 -89.13690618761969,40.60514561450119,0 -89.13691661801894,40.605153187183426,0 -89.13692704842056,40.60516075986466,0 -89.13693747882452,40.605168332544885,0 -89.13694790923084,40.60517590522408,0 -89.13663367271408,40.60600809947704,0 -89.13662373675413,40.60601604910218,0 -89.1366138007918,40.6060239987265,0 -89.13660386482715,40.60603194835003,0 -89.13659392886012,40.606039897972735,0 -89.13658399289076,40.60604784759466,0 -89.13689575628177,40.60722404184902,0 -89.13690618701348,40.60723161452152,0 -89.13691661774754,40.60723918719302,0 -89.13692704848395,40.60724675986351,0 -89.13693747922272,40.607254332532975,0 -89.13694790996385,40.60726190520142,0 -89.13694667367966,40.608266099390285,0 -89.13693673743494,40.60827404904696,0 -89.13692680118787,40.60828199870285,0 -89.13691686493846,40.60828994835794,0 -89.13690692868667,40.60829789801221,0 -89.13689699243255,40.608305847665676,0 -89.13557990799106,40.60726190557459,0 -89.13556947739912,40.60725433277859,0 -89.13555904680953,40.60724675998158,0 -89.1355486162223,40.60723918718355,0 -89.1355381856374,40.60723161438452,0 -89.13552775505488,40.60722404158448,0 -89.1353609910668,40.60609684738951,0 -89.13537092719277,40.60608889786913,0 -89.13538086331639,40.60608094834792,0 -89.13539079943764,40.606072998825915,0 -89.13540073555654,40.6060650493031,0 -89.1354106716731,40.60605709977948,0 -89.13557990725789,40.605175905597235,0 -89.13556947700073,40.60516833279051,0 -89.13555904674594,40.60516075998275,0 -89.1355486164935,40.60515318717398,0 -89.1355381862434,40.60514561436422,0 -89.13552775599565,40.60513804155344,0 -89.13552899211423,40.60413384740177,0 -89.13553892793882,40.60412589787222,0 -89.13554886376107,40.60411794834187,0 -89.13555879958098,40.60410999881072,0 -89.13556873539852,40.60410204927875,0 -89.13557867121371,40.604094099746,0 -89.13434990519077,40.604336905941814,0 -89.13433947520238,40.60432933301609,0 -89.13432904521635,40.60432176008935,0 -89.13431861523267,40.60431418716162,0 -89.13430818525134,40.604306614232854,0 -89.13429775527236,40.60429904130309,0 -89.13455267125163,40.60709109998752,0 -89.1345427348822,40.607099049442375,0 -89.13453279851042,40.60710699889642,0 -89.13452286213628,40.60711494834967,0 -89.1345129257598,40.60712289780212,0 -89.13450298938095,40.60713084725374,0',
            },
            styleUrl: '#navigation_path',
          },
        },
        {
          name: 'Regular',
          open: 1,
          Folder: [
            {
              name: 'centroids',
              open: 0,
              visibility: 1,
              Placemark: [
                {
                  visibility: 1,
                  name: 1,
                  Point: {
                    coordinates: '-89.13829,40.608286,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'centroid',
                      },
                      {
                        value: 1,
                        name: 'order',
                      },
                      {
                        value: '932300 16 8 703',
                        name: 'bag_id',
                      },
                      {
                        value: 1,
                        name: 'original_sample_id',
                      },
                      {
                        value: '',
                        name: 'external_reference_id',
                      },
                      {
                        value: '1970-01-01T00:00:00.000Z',
                        name: 'sampled_at',
                      },
                      {
                        value: '2024-07-22T14:57:58.706Z',
                        name: 'scanned_at',
                      },
                      {
                        value: '2024-07-22T14:57:58.706Z',
                        name: 'pulled_at',
                      },
                      {
                        value: '8f88afe3-6e5f-45f8-b89e-45963459bd10',
                        name: 'spec_id',
                      },
                      {
                        value: 6,
                        name: 'cores',
                      },
                      {
                        value: '5CGZASF1S4',
                        name: 'box_uid',
                      },
                      {
                        value: 'christopherp',
                        name: 'operator',
                      },
                      {
                        value: 2,
                        name: 'pattern_type',
                      },
                      {
                        value: 20,
                        name: 'radius',
                      },
                      {
                        value: 20,
                        name: 'length',
                      },
                      {
                        value: 45,
                        name: 'angle',
                      },
                      {
                        value: 0,
                        name: 'start_depth',
                      },
                      {
                        value: 6.67,
                        name: 'end_depth',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                    ],
                  },
                  styleUrl: '#centroid',
                },
                {
                  visibility: 1,
                  name: 2,
                  Point: {
                    coordinates: '-89.13829,40.607243,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'centroid',
                      },
                      {
                        value: 2,
                        name: 'order',
                      },
                      {
                        value: '932300 16 8 702',
                        name: 'bag_id',
                      },
                      {
                        value: 2,
                        name: 'original_sample_id',
                      },
                      {
                        value: '',
                        name: 'external_reference_id',
                      },
                      {
                        value: '1970-01-01T00:00:00.000Z',
                        name: 'sampled_at',
                      },
                      {
                        value: '2024-07-22T14:59:42.732Z',
                        name: 'scanned_at',
                      },
                      {
                        value: '2024-07-22T14:59:42.732Z',
                        name: 'pulled_at',
                      },
                      {
                        value: '8f88afe3-6e5f-45f8-b89e-45963459bd10',
                        name: 'spec_id',
                      },
                      {
                        value: 6,
                        name: 'cores',
                      },
                      {
                        value: '5CGZASF1S4',
                        name: 'box_uid',
                      },
                      {
                        value: 'christopherp',
                        name: 'operator',
                      },
                      {
                        value: 2,
                        name: 'pattern_type',
                      },
                      {
                        value: 20,
                        name: 'radius',
                      },
                      {
                        value: 20,
                        name: 'length',
                      },
                      {
                        value: 45,
                        name: 'angle',
                      },
                      {
                        value: 0,
                        name: 'start_depth',
                      },
                      {
                        value: 6.67,
                        name: 'end_depth',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                    ],
                  },
                  styleUrl: '#centroid',
                },
                {
                  visibility: 1,
                  name: 3,
                  Point: {
                    coordinates: '-89.138218,40.606028,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'centroid',
                      },
                      {
                        value: 3,
                        name: 'order',
                      },
                      {
                        value: '932300 16 8 701',
                        name: 'bag_id',
                      },
                      {
                        value: 3,
                        name: 'original_sample_id',
                      },
                      {
                        value: '',
                        name: 'external_reference_id',
                      },
                      {
                        value: '1970-01-01T00:00:00.000Z',
                        name: 'sampled_at',
                      },
                      {
                        value: '2024-07-22T15:01:04.187Z',
                        name: 'scanned_at',
                      },
                      {
                        value: '2024-07-22T15:01:04.187Z',
                        name: 'pulled_at',
                      },
                      {
                        value: '8f88afe3-6e5f-45f8-b89e-45963459bd10',
                        name: 'spec_id',
                      },
                      {
                        value: 6,
                        name: 'cores',
                      },
                      {
                        value: '5CGZASF1S4',
                        name: 'box_uid',
                      },
                      {
                        value: 'christopherp',
                        name: 'operator',
                      },
                      {
                        value: 2,
                        name: 'pattern_type',
                      },
                      {
                        value: 20,
                        name: 'radius',
                      },
                      {
                        value: 20,
                        name: 'length',
                      },
                      {
                        value: 45,
                        name: 'angle',
                      },
                      {
                        value: 0,
                        name: 'start_depth',
                      },
                      {
                        value: 6.67,
                        name: 'end_depth',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                    ],
                  },
                  styleUrl: '#centroid',
                },
                {
                  visibility: 1,
                  name: 4,
                  Point: {
                    coordinates: '-89.13829,40.605157,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'centroid',
                      },
                      {
                        value: 4,
                        name: 'order',
                      },
                      {
                        value: '932300 16 8 875',
                        name: 'bag_id',
                      },
                      {
                        value: 4,
                        name: 'original_sample_id',
                      },
                      {
                        value: '',
                        name: 'external_reference_id',
                      },
                      {
                        value: '1970-01-01T00:00:00.000Z',
                        name: 'sampled_at',
                      },
                      {
                        value: '2024-07-22T15:02:18.893Z',
                        name: 'scanned_at',
                      },
                      {
                        value: '2024-07-22T15:02:18.893Z',
                        name: 'pulled_at',
                      },
                      {
                        value: '8f88afe3-6e5f-45f8-b89e-45963459bd10',
                        name: 'spec_id',
                      },
                      {
                        value: 6,
                        name: 'cores',
                      },
                      {
                        value: '5CGZASF1S4',
                        name: 'box_uid',
                      },
                      {
                        value: 'christopherp',
                        name: 'operator',
                      },
                      {
                        value: 2,
                        name: 'pattern_type',
                      },
                      {
                        value: 20,
                        name: 'radius',
                      },
                      {
                        value: 20,
                        name: 'length',
                      },
                      {
                        value: 45,
                        name: 'angle',
                      },
                      {
                        value: 0,
                        name: 'start_depth',
                      },
                      {
                        value: 6.67,
                        name: 'end_depth',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                    ],
                  },
                  styleUrl: '#centroid',
                },
                {
                  visibility: 1,
                  name: 5,
                  Point: {
                    coordinates: '-89.1381,40.604261,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'centroid',
                      },
                      {
                        value: 5,
                        name: 'order',
                      },
                      {
                        value: '932300 16 8 874',
                        name: 'bag_id',
                      },
                      {
                        value: 5,
                        name: 'original_sample_id',
                      },
                      {
                        value: '',
                        name: 'external_reference_id',
                      },
                      {
                        value: '1970-01-01T00:00:00.000Z',
                        name: 'sampled_at',
                      },
                      {
                        value: '2024-07-22T15:03:29.176Z',
                        name: 'scanned_at',
                      },
                      {
                        value: '2024-07-22T15:03:29.176Z',
                        name: 'pulled_at',
                      },
                      {
                        value: '8f88afe3-6e5f-45f8-b89e-45963459bd10',
                        name: 'spec_id',
                      },
                      {
                        value: 6,
                        name: 'cores',
                      },
                      {
                        value: '5CGZASF1S4',
                        name: 'box_uid',
                      },
                      {
                        value: 'christopherp',
                        name: 'operator',
                      },
                      {
                        value: 2,
                        name: 'pattern_type',
                      },
                      {
                        value: 20,
                        name: 'radius',
                      },
                      {
                        value: 20,
                        name: 'length',
                      },
                      {
                        value: 45,
                        name: 'angle',
                      },
                      {
                        value: 0,
                        name: 'start_depth',
                      },
                      {
                        value: 6.67,
                        name: 'end_depth',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                    ],
                  },
                  styleUrl: '#centroid',
                },
                {
                  visibility: 1,
                  name: 6,
                  Point: {
                    coordinates: '-89.136522,40.604041,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'centroid',
                      },
                      {
                        value: 6,
                        name: 'order',
                      },
                      {
                        value: '932300 16 8 873',
                        name: 'bag_id',
                      },
                      {
                        value: 6,
                        name: 'original_sample_id',
                      },
                      {
                        value: '',
                        name: 'external_reference_id',
                      },
                      {
                        value: '1970-01-01T00:00:00.000Z',
                        name: 'sampled_at',
                      },
                      {
                        value: '2024-07-22T15:05:06.005Z',
                        name: 'scanned_at',
                      },
                      {
                        value: '2024-07-22T15:05:06.005Z',
                        name: 'pulled_at',
                      },
                      {
                        value: '8f88afe3-6e5f-45f8-b89e-45963459bd10',
                        name: 'spec_id',
                      },
                      {
                        value: 6,
                        name: 'cores',
                      },
                      {
                        value: '5CGZASF1S4',
                        name: 'box_uid',
                      },
                      {
                        value: 'christopherp',
                        name: 'operator',
                      },
                      {
                        value: 2,
                        name: 'pattern_type',
                      },
                      {
                        value: 20,
                        name: 'radius',
                      },
                      {
                        value: 20,
                        name: 'length',
                      },
                      {
                        value: 45,
                        name: 'angle',
                      },
                      {
                        value: 0,
                        name: 'start_depth',
                      },
                      {
                        value: 6.67,
                        name: 'end_depth',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                    ],
                  },
                  styleUrl: '#centroid',
                },
                {
                  visibility: 1,
                  name: 7,
                  Point: {
                    coordinates: '-89.136921,40.605157,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'centroid',
                      },
                      {
                        value: 7,
                        name: 'order',
                      },
                      {
                        value: '932300 16 8 872',
                        name: 'bag_id',
                      },
                      {
                        value: 7,
                        name: 'original_sample_id',
                      },
                      {
                        value: '',
                        name: 'external_reference_id',
                      },
                      {
                        value: '1970-01-01T00:00:00.000Z',
                        name: 'sampled_at',
                      },
                      {
                        value: '2024-07-22T15:06:51.972Z',
                        name: 'scanned_at',
                      },
                      {
                        value: '2024-07-22T15:06:51.972Z',
                        name: 'pulled_at',
                      },
                      {
                        value: '8f88afe3-6e5f-45f8-b89e-45963459bd10',
                        name: 'spec_id',
                      },
                      {
                        value: 6,
                        name: 'cores',
                      },
                      {
                        value: '5CGZASF1S4',
                        name: 'box_uid',
                      },
                      {
                        value: 'christopherp',
                        name: 'operator',
                      },
                      {
                        value: 2,
                        name: 'pattern_type',
                      },
                      {
                        value: 20,
                        name: 'radius',
                      },
                      {
                        value: 20,
                        name: 'length',
                      },
                      {
                        value: 45,
                        name: 'angle',
                      },
                      {
                        value: 0,
                        name: 'start_depth',
                      },
                      {
                        value: 6.67,
                        name: 'end_depth',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                    ],
                  },
                  styleUrl: '#centroid',
                },
                {
                  visibility: 1,
                  name: 8,
                  Point: {
                    coordinates: '-89.136608,40.606028,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'centroid',
                      },
                      {
                        value: 8,
                        name: 'order',
                      },
                      {
                        value: '932300 16 8 871',
                        name: 'bag_id',
                      },
                      {
                        value: 8,
                        name: 'original_sample_id',
                      },
                      {
                        value: '',
                        name: 'external_reference_id',
                      },
                      {
                        value: '1970-01-01T00:00:00.000Z',
                        name: 'sampled_at',
                      },
                      {
                        value: '2024-07-22T15:08:09.193Z',
                        name: 'scanned_at',
                      },
                      {
                        value: '2024-07-22T15:08:09.193Z',
                        name: 'pulled_at',
                      },
                      {
                        value: '8f88afe3-6e5f-45f8-b89e-45963459bd10',
                        name: 'spec_id',
                      },
                      {
                        value: 6,
                        name: 'cores',
                      },
                      {
                        value: '5CGZASF1S4',
                        name: 'box_uid',
                      },
                      {
                        value: 'christopherp',
                        name: 'operator',
                      },
                      {
                        value: 2,
                        name: 'pattern_type',
                      },
                      {
                        value: 20,
                        name: 'radius',
                      },
                      {
                        value: 20,
                        name: 'length',
                      },
                      {
                        value: 45,
                        name: 'angle',
                      },
                      {
                        value: 0,
                        name: 'start_depth',
                      },
                      {
                        value: 6.67,
                        name: 'end_depth',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                    ],
                  },
                  styleUrl: '#centroid',
                },
                {
                  visibility: 1,
                  name: 9,
                  Point: {
                    coordinates: '-89.136921,40.607243,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'centroid',
                      },
                      {
                        value: 9,
                        name: 'order',
                      },
                      {
                        value: '932300 16 8 870',
                        name: 'bag_id',
                      },
                      {
                        value: 9,
                        name: 'original_sample_id',
                      },
                      {
                        value: '',
                        name: 'external_reference_id',
                      },
                      {
                        value: '1970-01-01T00:00:00.000Z',
                        name: 'sampled_at',
                      },
                      {
                        value: '2024-07-22T15:09:39.888Z',
                        name: 'scanned_at',
                      },
                      {
                        value: '2024-07-22T15:09:39.888Z',
                        name: 'pulled_at',
                      },
                      {
                        value: '8f88afe3-6e5f-45f8-b89e-45963459bd10',
                        name: 'spec_id',
                      },
                      {
                        value: 6,
                        name: 'cores',
                      },
                      {
                        value: '5CGZASF1S4',
                        name: 'box_uid',
                      },
                      {
                        value: 'christopherp',
                        name: 'operator',
                      },
                      {
                        value: 2,
                        name: 'pattern_type',
                      },
                      {
                        value: 20,
                        name: 'radius',
                      },
                      {
                        value: 20,
                        name: 'length',
                      },
                      {
                        value: 45,
                        name: 'angle',
                      },
                      {
                        value: 0,
                        name: 'start_depth',
                      },
                      {
                        value: 6.67,
                        name: 'end_depth',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                    ],
                  },
                  styleUrl: '#centroid',
                },
                {
                  visibility: 1,
                  name: 10,
                  Point: {
                    coordinates: '-89.136921,40.608286,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'centroid',
                      },
                      {
                        value: 10,
                        name: 'order',
                      },
                      {
                        value: '932300 16 8 869',
                        name: 'bag_id',
                      },
                      {
                        value: 10,
                        name: 'original_sample_id',
                      },
                      {
                        value: '',
                        name: 'external_reference_id',
                      },
                      {
                        value: '1970-01-01T00:00:00.000Z',
                        name: 'sampled_at',
                      },
                      {
                        value: '2024-07-22T15:10:55.526Z',
                        name: 'scanned_at',
                      },
                      {
                        value: '2024-07-22T15:10:55.526Z',
                        name: 'pulled_at',
                      },
                      {
                        value: '8f88afe3-6e5f-45f8-b89e-45963459bd10',
                        name: 'spec_id',
                      },
                      {
                        value: 6,
                        name: 'cores',
                      },
                      {
                        value: '5CGZASF1S4',
                        name: 'box_uid',
                      },
                      {
                        value: 'christopherp',
                        name: 'operator',
                      },
                      {
                        value: 2,
                        name: 'pattern_type',
                      },
                      {
                        value: 20,
                        name: 'radius',
                      },
                      {
                        value: 20,
                        name: 'length',
                      },
                      {
                        value: 45,
                        name: 'angle',
                      },
                      {
                        value: 0,
                        name: 'start_depth',
                      },
                      {
                        value: 6.67,
                        name: 'end_depth',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                    ],
                  },
                  styleUrl: '#centroid',
                },
                {
                  visibility: 1,
                  name: 11,
                  Point: {
                    coordinates: '-89.135553,40.607243,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'centroid',
                      },
                      {
                        value: 11,
                        name: 'order',
                      },
                      {
                        value: '932300 16 8 868',
                        name: 'bag_id',
                      },
                      {
                        value: 11,
                        name: 'original_sample_id',
                      },
                      {
                        value: '',
                        name: 'external_reference_id',
                      },
                      {
                        value: '1970-01-01T00:00:00.000Z',
                        name: 'sampled_at',
                      },
                      {
                        value: '2024-07-22T15:12:32.247Z',
                        name: 'scanned_at',
                      },
                      {
                        value: '2024-07-22T15:12:32.247Z',
                        name: 'pulled_at',
                      },
                      {
                        value: '8f88afe3-6e5f-45f8-b89e-45963459bd10',
                        name: 'spec_id',
                      },
                      {
                        value: 6,
                        name: 'cores',
                      },
                      {
                        value: '5CGZASF1S4',
                        name: 'box_uid',
                      },
                      {
                        value: 'christopherp',
                        name: 'operator',
                      },
                      {
                        value: 2,
                        name: 'pattern_type',
                      },
                      {
                        value: 20,
                        name: 'radius',
                      },
                      {
                        value: 20,
                        name: 'length',
                      },
                      {
                        value: 45,
                        name: 'angle',
                      },
                      {
                        value: 0,
                        name: 'start_depth',
                      },
                      {
                        value: 6.67,
                        name: 'end_depth',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                    ],
                  },
                  styleUrl: '#centroid',
                },
                {
                  visibility: 1,
                  name: 12,
                  Point: {
                    coordinates: '-89.135385,40.606077,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'centroid',
                      },
                      {
                        value: 12,
                        name: 'order',
                      },
                      {
                        value: '932300 16 8 863',
                        name: 'bag_id',
                      },
                      {
                        value: 12,
                        name: 'original_sample_id',
                      },
                      {
                        value: '',
                        name: 'external_reference_id',
                      },
                      {
                        value: '1970-01-01T00:00:00.000Z',
                        name: 'sampled_at',
                      },
                      {
                        value: '2024-07-22T15:13:58.611Z',
                        name: 'scanned_at',
                      },
                      {
                        value: '2024-07-22T15:13:58.611Z',
                        name: 'pulled_at',
                      },
                      {
                        value: '8f88afe3-6e5f-45f8-b89e-45963459bd10',
                        name: 'spec_id',
                      },
                      {
                        value: 6,
                        name: 'cores',
                      },
                      {
                        value: '5CGZASF1S4',
                        name: 'box_uid',
                      },
                      {
                        value: 'christopherp',
                        name: 'operator',
                      },
                      {
                        value: 2,
                        name: 'pattern_type',
                      },
                      {
                        value: 20,
                        name: 'radius',
                      },
                      {
                        value: 20,
                        name: 'length',
                      },
                      {
                        value: 45,
                        name: 'angle',
                      },
                      {
                        value: 0,
                        name: 'start_depth',
                      },
                      {
                        value: 6.67,
                        name: 'end_depth',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                    ],
                  },
                  styleUrl: '#centroid',
                },
                {
                  visibility: 1,
                  name: 13,
                  Point: {
                    coordinates: '-89.135553,40.605157,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'centroid',
                      },
                      {
                        value: 13,
                        name: 'order',
                      },
                      {
                        value: '932300 16 8 864',
                        name: 'bag_id',
                      },
                      {
                        value: 13,
                        name: 'original_sample_id',
                      },
                      {
                        value: '',
                        name: 'external_reference_id',
                      },
                      {
                        value: '1970-01-01T00:00:00.000Z',
                        name: 'sampled_at',
                      },
                      {
                        value: '2024-07-22T15:15:20.016Z',
                        name: 'scanned_at',
                      },
                      {
                        value: '2024-07-22T15:15:20.016Z',
                        name: 'pulled_at',
                      },
                      {
                        value: '8f88afe3-6e5f-45f8-b89e-45963459bd10',
                        name: 'spec_id',
                      },
                      {
                        value: 6,
                        name: 'cores',
                      },
                      {
                        value: '5CGZASF1S4',
                        name: 'box_uid',
                      },
                      {
                        value: 'christopherp',
                        name: 'operator',
                      },
                      {
                        value: 2,
                        name: 'pattern_type',
                      },
                      {
                        value: 20,
                        name: 'radius',
                      },
                      {
                        value: 20,
                        name: 'length',
                      },
                      {
                        value: 45,
                        name: 'angle',
                      },
                      {
                        value: 0,
                        name: 'start_depth',
                      },
                      {
                        value: 6.67,
                        name: 'end_depth',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                    ],
                  },
                  styleUrl: '#centroid',
                },
                {
                  visibility: 1,
                  name: 14,
                  Point: {
                    coordinates: '-89.135553,40.604114,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'centroid',
                      },
                      {
                        value: 14,
                        name: 'order',
                      },
                      {
                        value: '932300 16 8 865',
                        name: 'bag_id',
                      },
                      {
                        value: 14,
                        name: 'original_sample_id',
                      },
                      {
                        value: '',
                        name: 'external_reference_id',
                      },
                      {
                        value: '1970-01-01T00:00:00.000Z',
                        name: 'sampled_at',
                      },
                      {
                        value: '2024-07-22T15:16:42.790Z',
                        name: 'scanned_at',
                      },
                      {
                        value: '2024-07-22T15:16:42.790Z',
                        name: 'pulled_at',
                      },
                      {
                        value: '8f88afe3-6e5f-45f8-b89e-45963459bd10',
                        name: 'spec_id',
                      },
                      {
                        value: 6,
                        name: 'cores',
                      },
                      {
                        value: '5CGZASF1S4',
                        name: 'box_uid',
                      },
                      {
                        value: 'christopherp',
                        name: 'operator',
                      },
                      {
                        value: 2,
                        name: 'pattern_type',
                      },
                      {
                        value: 20,
                        name: 'radius',
                      },
                      {
                        value: 20,
                        name: 'length',
                      },
                      {
                        value: 45,
                        name: 'angle',
                      },
                      {
                        value: 0,
                        name: 'start_depth',
                      },
                      {
                        value: 6.67,
                        name: 'end_depth',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                    ],
                  },
                  styleUrl: '#centroid',
                },
                {
                  visibility: 1,
                  name: 15,
                  Point: {
                    coordinates: '-89.134323,40.604318,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'centroid',
                      },
                      {
                        value: 15,
                        name: 'order',
                      },
                      {
                        value: '932300 16 8 866',
                        name: 'bag_id',
                      },
                      {
                        value: 15,
                        name: 'original_sample_id',
                      },
                      {
                        value: '',
                        name: 'external_reference_id',
                      },
                      {
                        value: '1970-01-01T00:00:00.000Z',
                        name: 'sampled_at',
                      },
                      {
                        value: '2024-07-22T15:18:05.382Z',
                        name: 'scanned_at',
                      },
                      {
                        value: '2024-07-22T15:18:05.382Z',
                        name: 'pulled_at',
                      },
                      {
                        value: '8f88afe3-6e5f-45f8-b89e-45963459bd10',
                        name: 'spec_id',
                      },
                      {
                        value: 6,
                        name: 'cores',
                      },
                      {
                        value: '5CGZASF1S4',
                        name: 'box_uid',
                      },
                      {
                        value: 'christopherp',
                        name: 'operator',
                      },
                      {
                        value: 2,
                        name: 'pattern_type',
                      },
                      {
                        value: 20,
                        name: 'radius',
                      },
                      {
                        value: 20,
                        name: 'length',
                      },
                      {
                        value: 45,
                        name: 'angle',
                      },
                      {
                        value: 0,
                        name: 'start_depth',
                      },
                      {
                        value: 6.67,
                        name: 'end_depth',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                    ],
                  },
                  styleUrl: '#centroid',
                },
                {
                  visibility: 1,
                  name: 16,
                  Point: {
                    coordinates: '-89.134527,40.60711100000001,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'centroid',
                      },
                      {
                        value: 16,
                        name: 'order',
                      },
                      {
                        value: '932300 16 8 867',
                        name: 'bag_id',
                      },
                      {
                        value: 16,
                        name: 'original_sample_id',
                      },
                      {
                        value: '',
                        name: 'external_reference_id',
                      },
                      {
                        value: '1970-01-01T00:00:00.000Z',
                        name: 'sampled_at',
                      },
                      {
                        value: '2024-07-22T15:20:35.805Z',
                        name: 'scanned_at',
                      },
                      {
                        value: '2024-07-22T15:20:35.805Z',
                        name: 'pulled_at',
                      },
                      {
                        value: '8f88afe3-6e5f-45f8-b89e-45963459bd10',
                        name: 'spec_id',
                      },
                      {
                        value: 6,
                        name: 'cores',
                      },
                      {
                        value: '5CGZASF1S4',
                        name: 'box_uid',
                      },
                      {
                        value: 'christopherp',
                        name: 'operator',
                      },
                      {
                        value: 2,
                        name: 'pattern_type',
                      },
                      {
                        value: 20,
                        name: 'radius',
                      },
                      {
                        value: 20,
                        name: 'length',
                      },
                      {
                        value: 45,
                        name: 'angle',
                      },
                      {
                        value: 0,
                        name: 'start_depth',
                      },
                      {
                        value: 6.67,
                        name: 'end_depth',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                    ],
                  },
                  styleUrl: '#centroid',
                },
              ],
            },
            {
              name: 'cores',
              open: 0,
              visibility: 1,
              Placemark: [
                {
                  visibility: 1,
                  name: 1.1,
                  Point: {
                    coordinates: '-89.13826475704097,40.608267042129285,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660278.706,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T14:57:58.706Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 0,
                        name: 'waypoint_index',
                      },
                      {
                        value: 1,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 1.2,
                  Point: {
                    coordinates: '-89.13827518808938,40.60827461466877,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660278.706,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T14:57:58.706Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 1,
                        name: 'waypoint_index',
                      },
                      {
                        value: 1,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 1.3,
                  Point: {
                    coordinates: '-89.13828561914015,40.60828218720725,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660278.706,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T14:57:58.706Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 2,
                        name: 'waypoint_index',
                      },
                      {
                        value: 1,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 1.4,
                  Point: {
                    coordinates: '-89.13829605019326,40.60828975974471,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660278.706,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T14:57:58.706Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 3,
                        name: 'waypoint_index',
                      },
                      {
                        value: 1,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 1.5,
                  Point: {
                    coordinates: '-89.13830648124873,40.60829733228117,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660278.706,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T14:57:58.706Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 4,
                        name: 'waypoint_index',
                      },
                      {
                        value: 1,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 1.6,
                  Point: {
                    coordinates: '-89.13831691230656,40.608304904816606,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 2,
                        name: 'command',
                      },
                      {
                        value: 1721660278.706,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T14:57:58.706Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 5,
                        name: 'waypoint_index',
                      },
                      {
                        value: 1,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 2.1,
                  Point: {
                    coordinates: '-89.13826599487929,40.60726284788717,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660382.732,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T14:59:42.732Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 6,
                        name: 'waypoint_index',
                      },
                      {
                        value: 2,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 2.2,
                  Point: {
                    coordinates: '-89.13827593081675,40.607254898122505,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660382.732,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T14:59:42.732Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 7,
                        name: 'waypoint_index',
                      },
                      {
                        value: 2,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 2.3,
                  Point: {
                    coordinates: '-89.13828586675184,40.60724694835703,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660382.732,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T14:59:42.732Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 8,
                        name: 'waypoint_index',
                      },
                      {
                        value: 2,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 2.4,
                  Point: {
                    coordinates: '-89.1382958026846,40.607238998590745,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660382.732,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T14:59:42.732Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 9,
                        name: 'waypoint_index',
                      },
                      {
                        value: 2,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 2.5,
                  Point: {
                    coordinates: '-89.13830573861497,40.60723104882366,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660382.732,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T14:59:42.732Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 10,
                        name: 'waypoint_index',
                      },
                      {
                        value: 2,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 2.6,
                  Point: {
                    coordinates: '-89.13831567454302,40.607223099055766,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 2,
                        name: 'command',
                      },
                      {
                        value: 1721660382.732,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T14:59:42.732Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 11,
                        name: 'waypoint_index',
                      },
                      {
                        value: 2,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 3.1,
                  Point: {
                    coordinates: '-89.13824491140926,40.60604690486079,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660464.187,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:01:04.187Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 12,
                        name: 'waypoint_index',
                      },
                      {
                        value: 3,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 3.2,
                  Point: {
                    coordinates: '-89.13823448072172,40.60603933230699,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660464.187,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:01:04.187Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 13,
                        name: 'waypoint_index',
                      },
                      {
                        value: 3,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 3.3,
                  Point: {
                    coordinates: '-89.13822405003654,40.60603175975218,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660464.187,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:01:04.187Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 14,
                        name: 'waypoint_index',
                      },
                      {
                        value: 3,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 3.4,
                  Point: {
                    coordinates: '-89.1382136193537,40.60602418719637,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660464.187,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:01:04.187Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 15,
                        name: 'waypoint_index',
                      },
                      {
                        value: 3,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 3.5,
                  Point: {
                    coordinates: '-89.13820318867323,40.606016614639536,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660464.187,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:01:04.187Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 16,
                        name: 'waypoint_index',
                      },
                      {
                        value: 3,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 3.6,
                  Point: {
                    coordinates: '-89.13819275799511,40.6060090420817,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 2,
                        name: 'command',
                      },
                      {
                        value: 1721660464.187,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:01:04.187Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 17,
                        name: 'waypoint_index',
                      },
                      {
                        value: 3,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 4.1,
                  Point: {
                    coordinates: '-89.13826599572951,40.60517684787011,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660538.893,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:02:18.893Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 18,
                        name: 'waypoint_index',
                      },
                      {
                        value: 4,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 4.2,
                  Point: {
                    coordinates: '-89.13827593136855,40.605168898110584,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660538.893,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:02:18.893Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 19,
                        name: 'waypoint_index',
                      },
                      {
                        value: 4,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 4.3,
                  Point: {
                    coordinates: '-89.13828586700525,40.60516094835024,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660538.893,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:02:18.893Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 20,
                        name: 'waypoint_index',
                      },
                      {
                        value: 4,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 4.4,
                  Point: {
                    coordinates: '-89.13829580263959,40.60515299858909,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660538.893,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:02:18.893Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 21,
                        name: 'waypoint_index',
                      },
                      {
                        value: 4,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 4.5,
                  Point: {
                    coordinates: '-89.13830573827157,40.60514504882713,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660538.893,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:02:18.893Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 22,
                        name: 'waypoint_index',
                      },
                      {
                        value: 4,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 4.6,
                  Point: {
                    coordinates: '-89.1383156739012,40.60513709906438,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 2,
                        name: 'command',
                      },
                      {
                        value: 1721660538.893,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:02:18.893Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 23,
                        name: 'waypoint_index',
                      },
                      {
                        value: 4,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 5.1,
                  Point: {
                    coordinates: '-89.1381269106181,40.604279904912154,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660609.176,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:03:29.176Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 24,
                        name: 'waypoint_index',
                      },
                      {
                        value: 5,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 5.2,
                  Point: {
                    coordinates: '-89.13811648022703,40.60427233233826,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660609.176,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:03:29.176Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 25,
                        name: 'waypoint_index',
                      },
                      {
                        value: 5,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 5.3,
                  Point: {
                    coordinates: '-89.1381060498383,40.60426475976334,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660609.176,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:03:29.176Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 26,
                        name: 'waypoint_index',
                      },
                      {
                        value: 5,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 5.4,
                  Point: {
                    coordinates: '-89.13809561945193,40.60425718718741,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660609.176,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:03:29.176Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 27,
                        name: 'waypoint_index',
                      },
                      {
                        value: 5,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 5.5,
                  Point: {
                    coordinates: '-89.13808518906792,40.60424961461047,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660609.176,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:03:29.176Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 28,
                        name: 'waypoint_index',
                      },
                      {
                        value: 5,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 5.6,
                  Point: {
                    coordinates: '-89.13807475868626,40.60424204203253,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 2,
                        name: 'command',
                      },
                      {
                        value: 1721660609.176,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:03:29.176Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 29,
                        name: 'waypoint_index',
                      },
                      {
                        value: 5,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 6.1,
                  Point: {
                    coordinates: '-89.13654767202817,40.6040210995065,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660706.005,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:05:06.005Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 30,
                        name: 'waypoint_index',
                      },
                      {
                        value: 6,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 6.2,
                  Point: {
                    coordinates: '-89.13653773634194,40.6040290491196,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660706.005,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:05:06.005Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 31,
                        name: 'waypoint_index',
                      },
                      {
                        value: 6,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 6.3,
                  Point: {
                    coordinates: '-89.13652780065334,40.6040369987319,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660706.005,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:05:06.005Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 32,
                        name: 'waypoint_index',
                      },
                      {
                        value: 6,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 6.4,
                  Point: {
                    coordinates: '-89.1365178649624,40.60404494834339,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660706.005,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:05:06.005Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 33,
                        name: 'waypoint_index',
                      },
                      {
                        value: 6,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 6.5,
                  Point: {
                    coordinates: '-89.1365079292691,40.6040528979541,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660706.005,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:05:06.005Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 34,
                        name: 'waypoint_index',
                      },
                      {
                        value: 6,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 6.6,
                  Point: {
                    coordinates: '-89.13649799357346,40.60406084756398,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 2,
                        name: 'command',
                      },
                      {
                        value: 1721660706.005,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:05:06.005Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 35,
                        name: 'waypoint_index',
                      },
                      {
                        value: 6,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 7.1,
                  Point: {
                    coordinates: '-89.13689575722277,40.60513804181795,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660811.972,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:06:51.972Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 36,
                        name: 'waypoint_index',
                      },
                      {
                        value: 7,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 7.2,
                  Point: {
                    coordinates: '-89.13690618761969,40.60514561450119,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660811.972,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:06:51.972Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 37,
                        name: 'waypoint_index',
                      },
                      {
                        value: 7,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 7.3,
                  Point: {
                    coordinates: '-89.13691661801894,40.605153187183426,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660811.972,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:06:51.972Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 38,
                        name: 'waypoint_index',
                      },
                      {
                        value: 7,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 7.4,
                  Point: {
                    coordinates: '-89.13692704842056,40.60516075986466,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660811.972,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:06:51.972Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 39,
                        name: 'waypoint_index',
                      },
                      {
                        value: 7,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 7.5,
                  Point: {
                    coordinates: '-89.13693747882452,40.605168332544885,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660811.972,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:06:51.972Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 40,
                        name: 'waypoint_index',
                      },
                      {
                        value: 7,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 7.6,
                  Point: {
                    coordinates: '-89.13694790923084,40.60517590522408,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 2,
                        name: 'command',
                      },
                      {
                        value: 1721660811.972,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:06:51.972Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 41,
                        name: 'waypoint_index',
                      },
                      {
                        value: 7,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 8.1,
                  Point: {
                    coordinates: '-89.13663367271408,40.60600809947704,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660889.193,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:08:09.193Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 42,
                        name: 'waypoint_index',
                      },
                      {
                        value: 8,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 8.2,
                  Point: {
                    coordinates: '-89.13662373675413,40.60601604910218,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660889.193,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:08:09.193Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 43,
                        name: 'waypoint_index',
                      },
                      {
                        value: 8,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 8.3,
                  Point: {
                    coordinates: '-89.1366138007918,40.6060239987265,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660889.193,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:08:09.193Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 44,
                        name: 'waypoint_index',
                      },
                      {
                        value: 8,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 8.4,
                  Point: {
                    coordinates: '-89.13660386482715,40.60603194835003,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660889.193,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:08:09.193Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 45,
                        name: 'waypoint_index',
                      },
                      {
                        value: 8,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 8.5,
                  Point: {
                    coordinates: '-89.13659392886012,40.606039897972735,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660889.193,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:08:09.193Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 46,
                        name: 'waypoint_index',
                      },
                      {
                        value: 8,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 8.6,
                  Point: {
                    coordinates: '-89.13658399289076,40.60604784759466,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 2,
                        name: 'command',
                      },
                      {
                        value: 1721660889.193,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:08:09.193Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 47,
                        name: 'waypoint_index',
                      },
                      {
                        value: 8,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 9.1,
                  Point: {
                    coordinates: '-89.13689575628177,40.60722404184902,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660979.888,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:09:39.888Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 48,
                        name: 'waypoint_index',
                      },
                      {
                        value: 9,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 9.2,
                  Point: {
                    coordinates: '-89.13690618701348,40.60723161452152,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660979.888,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:09:39.888Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 49,
                        name: 'waypoint_index',
                      },
                      {
                        value: 9,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 9.3,
                  Point: {
                    coordinates: '-89.13691661774754,40.60723918719302,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660979.888,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:09:39.888Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 50,
                        name: 'waypoint_index',
                      },
                      {
                        value: 9,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 9.4,
                  Point: {
                    coordinates: '-89.13692704848395,40.60724675986351,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660979.888,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:09:39.888Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 51,
                        name: 'waypoint_index',
                      },
                      {
                        value: 9,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 9.5,
                  Point: {
                    coordinates: '-89.13693747922272,40.607254332532975,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721660979.888,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:09:39.888Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 52,
                        name: 'waypoint_index',
                      },
                      {
                        value: 9,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 9.6,
                  Point: {
                    coordinates: '-89.13694790996385,40.60726190520142,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 2,
                        name: 'command',
                      },
                      {
                        value: 1721660979.888,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:09:39.888Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 53,
                        name: 'waypoint_index',
                      },
                      {
                        value: 9,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 10.1,
                  Point: {
                    coordinates: '-89.13694667367966,40.608266099390285,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661055.526,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:10:55.526Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 54,
                        name: 'waypoint_index',
                      },
                      {
                        value: 10,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 10.2,
                  Point: {
                    coordinates: '-89.13693673743494,40.60827404904696,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661055.526,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:10:55.526Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 55,
                        name: 'waypoint_index',
                      },
                      {
                        value: 10,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 10.3,
                  Point: {
                    coordinates: '-89.13692680118787,40.60828199870285,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661055.526,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:10:55.526Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 56,
                        name: 'waypoint_index',
                      },
                      {
                        value: 10,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 10.4,
                  Point: {
                    coordinates: '-89.13691686493846,40.60828994835794,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661055.526,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:10:55.526Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 57,
                        name: 'waypoint_index',
                      },
                      {
                        value: 10,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 10.5,
                  Point: {
                    coordinates: '-89.13690692868667,40.60829789801221,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661055.526,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:10:55.526Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 58,
                        name: 'waypoint_index',
                      },
                      {
                        value: 10,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 10.6,
                  Point: {
                    coordinates: '-89.13689699243255,40.608305847665676,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 2,
                        name: 'command',
                      },
                      {
                        value: 1721661055.526,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:10:55.526Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 59,
                        name: 'waypoint_index',
                      },
                      {
                        value: 10,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 11.1,
                  Point: {
                    coordinates: '-89.13557990799106,40.60726190557459,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661152.247,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:12:32.247Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 60,
                        name: 'waypoint_index',
                      },
                      {
                        value: 11,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 11.2,
                  Point: {
                    coordinates: '-89.13556947739912,40.60725433277859,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661152.247,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:12:32.247Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 61,
                        name: 'waypoint_index',
                      },
                      {
                        value: 11,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 11.3,
                  Point: {
                    coordinates: '-89.13555904680953,40.60724675998158,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661152.247,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:12:32.247Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 62,
                        name: 'waypoint_index',
                      },
                      {
                        value: 11,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 11.4,
                  Point: {
                    coordinates: '-89.1355486162223,40.60723918718355,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661152.247,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:12:32.247Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 63,
                        name: 'waypoint_index',
                      },
                      {
                        value: 11,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 11.5,
                  Point: {
                    coordinates: '-89.1355381856374,40.60723161438452,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661152.247,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:12:32.247Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 64,
                        name: 'waypoint_index',
                      },
                      {
                        value: 11,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 11.6,
                  Point: {
                    coordinates: '-89.13552775505488,40.60722404158448,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 2,
                        name: 'command',
                      },
                      {
                        value: 1721661152.247,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:12:32.247Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 65,
                        name: 'waypoint_index',
                      },
                      {
                        value: 11,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 12.1,
                  Point: {
                    coordinates: '-89.1353609910668,40.60609684738951,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661238.611,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:13:58.611Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 66,
                        name: 'waypoint_index',
                      },
                      {
                        value: 12,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 12.2,
                  Point: {
                    coordinates: '-89.13537092719277,40.60608889786913,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661238.611,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:13:58.611Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 67,
                        name: 'waypoint_index',
                      },
                      {
                        value: 12,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 12.3,
                  Point: {
                    coordinates: '-89.13538086331639,40.60608094834792,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661238.611,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:13:58.611Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 68,
                        name: 'waypoint_index',
                      },
                      {
                        value: 12,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 12.4,
                  Point: {
                    coordinates: '-89.13539079943764,40.606072998825915,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661238.611,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:13:58.611Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 69,
                        name: 'waypoint_index',
                      },
                      {
                        value: 12,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 12.5,
                  Point: {
                    coordinates: '-89.13540073555654,40.6060650493031,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661238.611,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:13:58.611Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 70,
                        name: 'waypoint_index',
                      },
                      {
                        value: 12,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 12.6,
                  Point: {
                    coordinates: '-89.1354106716731,40.60605709977948,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 2,
                        name: 'command',
                      },
                      {
                        value: 1721661238.611,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:13:58.611Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 71,
                        name: 'waypoint_index',
                      },
                      {
                        value: 12,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 13.1,
                  Point: {
                    coordinates: '-89.13557990725789,40.605175905597235,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661320.016,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:15:20.016Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 72,
                        name: 'waypoint_index',
                      },
                      {
                        value: 13,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 13.2,
                  Point: {
                    coordinates: '-89.13556947700073,40.60516833279051,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661320.016,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:15:20.016Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 73,
                        name: 'waypoint_index',
                      },
                      {
                        value: 13,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 13.3,
                  Point: {
                    coordinates: '-89.13555904674594,40.60516075998275,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661320.016,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:15:20.016Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 74,
                        name: 'waypoint_index',
                      },
                      {
                        value: 13,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 13.4,
                  Point: {
                    coordinates: '-89.1355486164935,40.60515318717398,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661320.016,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:15:20.016Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 75,
                        name: 'waypoint_index',
                      },
                      {
                        value: 13,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 13.5,
                  Point: {
                    coordinates: '-89.1355381862434,40.60514561436422,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661320.016,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:15:20.016Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 76,
                        name: 'waypoint_index',
                      },
                      {
                        value: 13,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 13.6,
                  Point: {
                    coordinates: '-89.13552775599565,40.60513804155344,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 2,
                        name: 'command',
                      },
                      {
                        value: 1721661320.016,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:15:20.016Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 77,
                        name: 'waypoint_index',
                      },
                      {
                        value: 13,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 14.1,
                  Point: {
                    coordinates: '-89.13552899211423,40.60413384740177,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661402.79,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:16:42.790Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 78,
                        name: 'waypoint_index',
                      },
                      {
                        value: 14,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 14.2,
                  Point: {
                    coordinates: '-89.13553892793882,40.60412589787222,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661402.79,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:16:42.790Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 79,
                        name: 'waypoint_index',
                      },
                      {
                        value: 14,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 14.3,
                  Point: {
                    coordinates: '-89.13554886376107,40.60411794834187,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661402.79,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:16:42.790Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 80,
                        name: 'waypoint_index',
                      },
                      {
                        value: 14,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 14.4,
                  Point: {
                    coordinates: '-89.13555879958098,40.60410999881072,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661402.79,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:16:42.790Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 81,
                        name: 'waypoint_index',
                      },
                      {
                        value: 14,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 14.5,
                  Point: {
                    coordinates: '-89.13556873539852,40.60410204927875,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661402.79,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:16:42.790Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 82,
                        name: 'waypoint_index',
                      },
                      {
                        value: 14,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 14.6,
                  Point: {
                    coordinates: '-89.13557867121371,40.604094099746,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 2,
                        name: 'command',
                      },
                      {
                        value: 1721661402.79,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:16:42.790Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 83,
                        name: 'waypoint_index',
                      },
                      {
                        value: 14,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 15.1,
                  Point: {
                    coordinates: '-89.13434990519077,40.604336905941814,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661485.382,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:18:05.382Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 84,
                        name: 'waypoint_index',
                      },
                      {
                        value: 15,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 15.2,
                  Point: {
                    coordinates: '-89.13433947520238,40.60432933301609,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661485.382,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:18:05.382Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 85,
                        name: 'waypoint_index',
                      },
                      {
                        value: 15,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 15.3,
                  Point: {
                    coordinates: '-89.13432904521635,40.60432176008935,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661485.382,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:18:05.382Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 86,
                        name: 'waypoint_index',
                      },
                      {
                        value: 15,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 15.4,
                  Point: {
                    coordinates: '-89.13431861523267,40.60431418716162,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661485.382,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:18:05.382Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 87,
                        name: 'waypoint_index',
                      },
                      {
                        value: 15,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 15.5,
                  Point: {
                    coordinates: '-89.13430818525134,40.604306614232854,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661485.382,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:18:05.382Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 88,
                        name: 'waypoint_index',
                      },
                      {
                        value: 15,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 15.6,
                  Point: {
                    coordinates: '-89.13429775527236,40.60429904130309,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 2,
                        name: 'command',
                      },
                      {
                        value: 1721661485.382,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:18:05.382Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 89,
                        name: 'waypoint_index',
                      },
                      {
                        value: 15,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 16.1,
                  Point: {
                    coordinates: '-89.13455267125163,40.60709109998752,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661635.805,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:20:35.805Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 90,
                        name: 'waypoint_index',
                      },
                      {
                        value: 16,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 16.2,
                  Point: {
                    coordinates: '-89.1345427348822,40.607099049442375,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661635.805,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:20:35.805Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 91,
                        name: 'waypoint_index',
                      },
                      {
                        value: 16,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 16.3,
                  Point: {
                    coordinates: '-89.13453279851042,40.60710699889642,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661635.805,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:20:35.805Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 92,
                        name: 'waypoint_index',
                      },
                      {
                        value: 16,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 16.4,
                  Point: {
                    coordinates: '-89.13452286213628,40.60711494834967,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661635.805,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:20:35.805Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 93,
                        name: 'waypoint_index',
                      },
                      {
                        value: 16,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 16.5,
                  Point: {
                    coordinates: '-89.1345129257598,40.60712289780212,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 1,
                        name: 'command',
                      },
                      {
                        value: 1721661635.805,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:20:35.805Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 94,
                        name: 'waypoint_index',
                      },
                      {
                        value: 16,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
                {
                  visibility: 1,
                  name: 16.6,
                  Point: {
                    coordinates: '-89.13450298938095,40.60713084725374,0',
                  },
                  ExtendedData: {
                    Data: [
                      {
                        value: true,
                        name: 'core_location',
                      },
                      {
                        value: 2,
                        name: 'command',
                      },
                      {
                        value: 1721661635.805,
                        name: 'pulled_at_timestamp',
                      },
                      {
                        value: '2024-07-22T15:20:35.805Z',
                        name: 'pulled_at',
                      },
                      {
                        value: 95,
                        name: 'waypoint_index',
                      },
                      {
                        value: 16,
                        name: 'sample_id',
                      },
                      {
                        value: 'None',
                        name: 'change_type',
                      },
                      {
                        value: '',
                        name: 'change_reason',
                      },
                    ],
                  },
                  styleUrl: '#core_location',
                },
              ],
            },
          ],
        },
      ],
    },
    Style: [
      {
        LineStyle: {
          color: 'ffff0000',
          width: 2,
        },
        PolyStyle: {
          fill: 1,
          color: '00ff0000',
        },
        id: 'field_boundary',
      },
      {
        LineStyle: {
          color: 'ffffac00',
          width: 2,
        },
        PolyStyle: {
          fill: 1,
          color: '00ffac00',
        },
        id: 'collected_field_boundary',
      },
      {
        LineStyle: {
          color: 'ffffffff',
          width: 1,
        },
        PolyStyle: {
          fill: 1,
          color: '4d0000ff',
        },
        id: 'unsafe_zone',
      },
      {
        LineStyle: {
          color: 'ffffffff',
          width: 1,
        },
        PolyStyle: {
          fill: 1,
          color: '4d00ffff',
        },
        id: 'slow_zone',
      },
      {
        LineStyle: {
          color: 'ffffffff',
          width: 1,
        },
        PolyStyle: {
          fill: 1,
          color: '4d00ff00',
        },
        id: 'generic_zone',
      },
      {
        LineStyle: {
          color: 'ffffffff',
          width: 1,
        },
        id: 'navigation_path',
      },
      {
        IconStyle: {
          scale: 1,
          Icon: {
            href: 'http://maps.google.com/mapfiles/kml/pushpin/ylw-pushpin.png',
          },
          hotSpot: {
            x: 20,
            y: 2,
            xunits: 'pixels',
            yunits: 'pixels',
          },
        },
        LabelStyle: {
          color: '00ffffff',
        },
        id: 'core_normal',
      },
      {
        IconStyle: {
          scale: 1.1,
          Icon: {
            href: 'http://maps.google.com/mapfiles/kml/pushpin/ylw-pushpin.png',
          },
          hotSpot: {
            x: 20,
            y: 2,
            xunits: 'pixels',
            yunits: 'pixels',
          },
        },
        id: 'core_highlight',
      },
      {
        IconStyle: {
          scale: 1,
          Icon: {
            href: 'http://maps.google.com/mapfiles/kml/pushpin/blue-pushpin.png',
          },
          hotSpot: {
            x: 20,
            y: 2,
            xunits: 'pixels',
            yunits: 'pixels',
          },
        },
        LabelStyle: {
          color: '00ffffff',
        },
        id: 'taken_core_normal',
      },
      {
        IconStyle: {
          scale: 1.1,
          Icon: {
            href: 'http://maps.google.com/mapfiles/kml/pushpin/blue-pushpin.png',
          },
          hotSpot: {
            x: 20,
            y: 2,
            xunits: 'pixels',
            yunits: 'pixels',
          },
        },
        id: 'taken_core_highlight',
      },
      {
        IconStyle: {
          scale: 1,
          Icon: {
            href: 'http://maps.google.com/mapfiles/kml/shapes/placemark_circle.png',
          },
        },
        id: 'centroid_normal',
      },
      {
        IconStyle: {
          scale: 1.1,
          Icon: {
            href: 'http://maps.google.com/mapfiles/kml/shapes/placemark_circle_highlight.png',
          },
        },
        id: 'centroid_highlight',
      },
    ],
    StyleMap: [
      {
        Pair: [
          {
            key: 'normal',
            styleUrl: '#core_normal',
          },
          {
            key: 'highlight',
            styleUrl: '#core_highlight',
          },
        ],
        id: 'core_location',
      },
      {
        Pair: [
          {
            key: 'normal',
            styleUrl: '#taken_core_normal',
          },
          {
            key: 'highlight',
            styleUrl: '#taken_core_highlight',
          },
        ],
        id: 'taken_core_location',
      },
      {
        Pair: [
          {
            key: 'normal',
            styleUrl: '#centroid_normal',
          },
          {
            key: 'highlight',
            styleUrl: '#centroid_highlight',
          },
        ],
        id: 'centroid',
      },
    ],
  };

  return kmlToString(sampleDoc);
}
